/* eslint eslint-comments/no-use: off */
// eslint-disable-next-line @github-ui/github-monorepo/filename-convention
import {VALUES} from '@github-ui/item-picker-base/constants/values'
import {
  getIssueSearchQueries,
  getSemanticallySimilarQuery,
  getSemanticSearchType,
} from '@github-ui/item-picker-base/shared'
import {clientSideRelayFetchQueryRetained} from '@github-ui/relay-environment'
import {useDebounce} from '@github-ui/use-debounce'
import {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {graphql, useRelayEnvironment} from 'react-relay'

import type {IssuePickerItem} from '../components/IssuePicker'
import type {useIssueFilteringQuery, useIssueFilteringQuery$data} from './__generated__/useIssueFilteringQuery.graphql'

// The `semanticallySimilar` bucket is capped at `first: 5` (below the lexical buckets' `first: 10`)
// so strong semantic matches surface without burying the recent-interaction suggestions. Tune with
// engagement data.
// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
export const useIssueFilteringQueryGraphQLQuery = graphql`
  query useIssueFilteringQuery(
    $commenters: String!
    $mentions: String!
    $assignee: String!
    $author: String!
    $other: String!
    $first: Int = 10
    $resource: URI!
    $queryIsUrl: Boolean!
    $withParent: Boolean!
    $requireTargetTriage: Boolean = false
    $semanticallySimilarQuery: String = ""
    $includeSemanticallySimilar: Boolean = false
    $semanticSearchType: SearchType = ISSUE_ADVANCED
  ) {
    semanticallySimilar: search(type: $semanticSearchType, first: 5, query: $semanticallySimilarQuery)
      @include(if: $includeSemanticallySimilar) {
      nodes {
        ... on Issue {
          id
          title
          state
          stateReason
          repository {
            id
            nameWithOwner
          }
          databaseId
          number
          url
          viewerCanUpdateMetadata @include(if: $requireTargetTriage)
          __typename
          parent @include(if: $withParent) {
            id
          }
        }
      }
    }
    commenters: search(type: ISSUE, first: $first, query: $commenters) {
      nodes {
        ... on Issue {
          id
          title
          state
          stateReason
          repository {
            id
            nameWithOwner
          }
          databaseId
          number
          url
          viewerCanUpdateMetadata @include(if: $requireTargetTriage)
          __typename
          parent @include(if: $withParent) {
            id
          }
        }
      }
    }
    mentions: search(type: ISSUE, first: $first, query: $mentions) {
      nodes {
        ... on Issue {
          id
          title
          state
          stateReason
          repository {
            id
            nameWithOwner
          }
          databaseId
          number
          url
          viewerCanUpdateMetadata @include(if: $requireTargetTriage)
          __typename
          parent @include(if: $withParent) {
            id
          }
        }
      }
    }
    assignee: search(type: ISSUE, first: $first, query: $assignee) {
      nodes {
        ... on Issue {
          id
          title
          state
          stateReason
          repository {
            id
            nameWithOwner
          }
          databaseId
          number
          url
          viewerCanUpdateMetadata @include(if: $requireTargetTriage)
          __typename
          parent @include(if: $withParent) {
            id
          }
        }
      }
    }
    author: search(type: ISSUE, first: $first, query: $author) {
      nodes {
        ... on Issue {
          id
          title
          state
          stateReason
          repository {
            id
            nameWithOwner
          }
          databaseId
          number
          url
          viewerCanUpdateMetadata @include(if: $requireTargetTriage)
          __typename
          parent @include(if: $withParent) {
            id
          }
        }
      }
    }
    other: search(type: ISSUE, first: $first, query: $other) {
      nodes {
        ... on Issue {
          id
          title
          state
          stateReason
          repository {
            id
            nameWithOwner
          }
          databaseId
          number
          url
          viewerCanUpdateMetadata @include(if: $requireTargetTriage)
          __typename
          parent @include(if: $withParent) {
            id
          }
        }
      }
    }
    # Do not return resource unless it is an Issue
    resource: resource(url: $resource) @include(if: $queryIsUrl) {
      ... on Issue {
        id
        title
        state
        stateReason
        repository {
          id
          nameWithOwner
        }
        databaseId
        number
        url
        viewerCanUpdateMetadata @include(if: $requireTargetTriage)
        __typename
        parent @include(if: $withParent) {
          id
        }
      }
    }
  }
`

// Handles debouncing queries to fetch issues based on the current filter
export const useIssueFiltering = (
  filter: string,
  owner: string = '',
  repositoryNameWithOwner?: string,
  hiddenIssueIds?: string[],
  withParent: boolean = false,
  requireTargetTriage: boolean = false,
  // Issue title used to surface semantically similar suggestions when the filter is empty.
  semanticQuery?: string,
): {
  isLoading: boolean
  isError: boolean
  items: Map<string, IssuePickerItem>
  // Ids of the issues that came from the semantic bucket, so callers can attribute a selection to
  // semantic vs lexical search for engagement telemetry.
  semanticIssueIds: Set<string>
} => {
  const environment = useRelayEnvironment()

  const [isLoading, setIsLoading] = useState(true)
  const [isError, setIsError] = useState(false)
  const [data, setData] = useState<useIssueFilteringQuery$data | null>(null)

  const activeSubscriptionRef = useRef<{unsubscribe: () => void} | null>(null)

  const semanticSearchType = getSemanticSearchType(environment)

  const fetchSearchData = useCallback(
    (nextFilter: string) => {
      setIsLoading(true)

      // Cancel any existing subscription to prevent race conditions
      activeSubscriptionRef.current?.unsubscribe()

      const semanticallySimilarQuery = semanticSearchType
        ? getSemanticallySimilarQuery(owner, semanticQuery, repositoryNameWithOwner)
        : ''
      // Semantic search is expensive, so only request it for the empty-input (suggestions) state.
      const includeSemanticallySimilar = Boolean(semanticallySimilarQuery) && nextFilter.trim() === ''

      const subscription = clientSideRelayFetchQueryRetained<useIssueFilteringQuery>({
        environment,
        query: useIssueFilteringQueryGraphQLQuery,
        variables: {
          ...getIssueSearchQueries(owner, nextFilter, repositoryNameWithOwner),
          withParent,
          requireTargetTriage,
          semanticallySimilarQuery,
          includeSemanticallySimilar,
          // Only sent when the bucket is included, so the `ISSUE_ADVANCED` default applies elsewhere.
          ...(includeSemanticallySimilar && semanticSearchType ? {semanticSearchType} : {}),
        },
      }).subscribe({
        next: (innerData: useIssueFilteringQuery$data) => {
          setData(innerData ?? null)
          setIsLoading(false)
          setIsError(false)
        },
        error: () => {
          setIsError(true)
          setIsLoading(false)
        },
      })

      activeSubscriptionRef.current = subscription
    },
    [environment, owner, repositoryNameWithOwner, withParent, requireTargetTriage, semanticQuery, semanticSearchType],
  )

  const debounceFetchSearchData = useDebounce(
    (nextValue: string) => fetchSearchData(nextValue),
    VALUES.pickerDebounceTime,
  )

  // `semanticQuery` is a dep because the title can arrive after the first fetch.
  useEffect(() => {
    debounceFetchSearchData(filter)
  }, [debounceFetchSearchData, filter, semanticQuery])

  useEffect(() => {
    return () => {
      // Always clean up subscription on unmount to prevent memory leaks
      activeSubscriptionRef.current?.unsubscribe()
    }
  }, [])

  const items: Map<string, IssuePickerItem> = useMemo(() => {
    if (!data) {
      return new Map()
    } else {
      // When resource is returned, we only want to show that issue as the user's intent is to
      // select a specific issue otherwise, we show all the issues from the search queries
      if (data.resource?.__typename === 'Issue') {
        // Best-effort hide of a directly-resolved issue the viewer can't triage (viewerCanUpdateMetadata
        // reflects base repo role); the mutation-level FORBIDDEN handling is the authoritative backstop.
        if (requireTargetTriage && data.resource.viewerCanUpdateMetadata !== true) {
          return new Map()
        }
        return new Map([[data.resource?.id, data.resource]])
      }

      // Order the unique issues from each of the queries. Changing the order of this spread
      // will change the relative priority of each individual search query. Semantically similar
      // results are placed first so they surface at the top.
      const searchResultNodes = [
        ...(data.semanticallySimilar?.nodes || []),
        ...(data.commenters?.nodes || []),
        ...(data.mentions?.nodes || []),
        ...(data.assignee?.nodes || []),
        ...(data.author?.nodes || []),
        ...(data.other?.nodes || []),
      ]

      // viewerCanUpdateMetadata is only @include'd when requireTargetTriage is true, so guard the read.
      const issueNodes = searchResultNodes.filter(
        (node): node is IssuePickerItem =>
          node?.__typename === 'Issue' && (!requireTargetTriage || node.viewerCanUpdateMetadata === true),
      )

      const issuesMap = new Map<string, IssuePickerItem>()
      for (const issue of issueNodes) {
        if (hiddenIssueIds?.some(id => id === issue.id)) {
          continue
        }

        issuesMap.set(issue.id, issue)
      }

      return issuesMap
    }
  }, [hiddenIssueIds, data, requireTargetTriage])

  const semanticIssueIds = useMemo(() => {
    const ids = new Set<string>()
    for (const node of data?.semanticallySimilar?.nodes || []) {
      if (node?.__typename === 'Issue') ids.add(node.id)
    }
    return ids
  }, [data])

  return {isLoading, isError, items, semanticIssueIds}
}
